import { type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { analytics } from '@/analytics';
import { Button } from '@/components/atoms/Buttons';
import { EmptyState } from '@/components/molecules/EmptyState';

interface SomethingHappenedProps {
    error?: unknown;
    /** Replaces the generic "try refreshing the page" heading with one that names what failed. */
    title?: ReactNode;
    /** Offers a retry button, so the user does not have to reload the whole page. */
    onRetry?: () => void;
    /** Disables the retry button while the retried request is in flight. */
    isRetrying?: boolean;
    /** `h1` when the error stands in for the whole page, so the page still has a title. */
    headingLevel?: 'h1' | 'h2' | 'h3';
}

/**
 * What a page or section shows in place of content that failed to load: the error variant
 * of EmptyState, reported to analytics once.
 *
 * The other half of the error pattern is an inline `Alert tone="danger"` beside the control
 * whose action failed (a save, a delete, an export), which leaves the content on screen.
 */
function SomethingHappened(props: SomethingHappenedProps) {
    const { title, onRetry, isRetrying = false, headingLevel } = props;
    const { t } = useTranslation();
    useEffect(() => {
        analytics.errorEncountered({ errorType: 'query' });
    }, []);

    return (
        <EmptyState
            variant="error"
            size="spacious"
            headingLevel={headingLevel}
            title={title ?? t('common.labels.somethingHappened')}
            action={
                onRetry ? (
                    <Button variant="secondary" onClick={onRetry} disabled={isRetrying}>
                        {t('common.actions.retry')}
                    </Button>
                ) : undefined
            }
        />
    );
}

export default SomethingHappened;
